import {
    Box,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Typography,
    Tooltip,
    Divider,
    CardContent,
} from "@mui/material";
import { AppCard } from '../../components/common/AppCard';
import { RecordContextProvider } from "react-admin";

// Material UI Icons
import TagIcon from "@mui/icons-material/Numbers"; // Positive P&L
import TrendingDownIcon from "@mui/icons-material/TrendingDown"; // Negative P&L
import LocalAtmIcon from "@mui/icons-material/LocalAtm"; // Price at Settlement
import CalendarTodayIcon from "@mui/icons-material/Today"; // Date
import ShowChartIcon from "@mui/icons-material/ShowChart"; // Amount Sold
import ClassIcon from '@mui/icons-material/Class';
import QueryStatsIcon from "@mui/icons-material/QueryStats"; // Realized P&L
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined"; // Settlement Valuation
import RedeemIcon from "@mui/icons-material/Payments"; // For "reward"
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/DownloadDone';
import { Settlement } from "../../types/types";
import { getFormattedDate, getFormattedTime } from "../../types/util";
import { useNavigate } from "react-router-dom";


interface Props {
    settlements: Settlement[];
    funds: Record<string, string>; // fundId → fundName mapping
}

export const PortfolioSettlements = ({ settlements, funds }: Props) => {
    // Get user-selected currency from local storage or use default "EUR"
    const currency: "EUR" | "USD" | "CHF" =
        (localStorage.getItem("currency") as "EUR" | "USD" | "CHF") || "EUR";

    const navigate = useNavigate();

    // Function to get the correct icon for settlement origin
    const getSettlementOriginIcon = (origin: Settlement["origin"]) => {
        switch (origin) {
            case "reward":
                return <RedeemIcon color="primary" fontSize="small" />;
            case "withdrawal":
                return <CallReceivedIcon color="primary" fontSize="small" />;
            default:
                return <></>;
        }
    };

    return (
        <>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DownloadIcon fontSize="small" />
                Settlements
            </Typography>
            <AppCard>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels with Icons */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                        <Grid item xs={4}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ClassIcon fontSize="small" /> Asset
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PriceChangeIcon fontSize="small" /> Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocalAtmIcon fontSize="small" /> Value
                            </Typography>
                        </Grid>

                        <Grid item xs={1.5} >
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <QueryStatsIcon fontSize="small" /> P&L
                            </Typography>
                        </Grid>

                        <Grid item xs={1.5} >
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <TagIcon fontSize="small" />
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                <CalendarTodayIcon fontSize="small" />
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Settlement Data */}
                    {settlements.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                No settlements available.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {settlements.map((settlement, index) => {

                                const fundName = funds[settlement.asset_id] ?? settlement.asset_id; // Use fund name if available

                                return (
                                    <RecordContextProvider key={settlement.id} value={settlement}>
                                        <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                            <ListItemButton
                                                onClick={() => navigate(`/transactions/${settlement.transaction}/show`)}
                                                sx={{
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                    paddingY: 0.75,
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                }}
                                            >
                                                {/* Settlement Details Grid */}
                                                <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>

                                                    {/* Asset Name */}
                                                    <Grid item xs={4}>
                                                        <Typography variant="body1" color="textPrimary" sx={{ fontWeight: "bold" }}>
                                                            {fundName}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Amount Sold */}
                                                    <Grid item xs={2}>
                                                        {settlement.amount === 0 ? (
                                                            <Typography variant="body2" color="textPrimary">-</Typography>
                                                        ) : (
                                                            <>
                                                                <Typography variant="body2" color="textPrimary">
                                                                    {settlement.amount.toFixed(2)}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {settlement.price?.global?.[currency] ? settlement.price.global[currency].toFixed(2) : "N/A"} {currency}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Grid>

                                                    {/* Price at Settlement */}
                                                    <Grid item xs={2}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {settlement.valuation?.global?.[currency] ? settlement.valuation.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                        {settlement.origin !== "reward" && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {settlement.cost_basis?.global?.[currency] ? settlement.cost_basis.global[currency].toFixed(2) : "N/A"} {currency}
                                                            </Typography>
                                                        )}
                                                    </Grid>

                                                    {/* Realized P&L */}
                                                    <Grid item xs={1.5} >
                                                        <Typography
                                                            variant="body2"
                                                            color={(settlement.realized_pnl?.global?.[currency] ?? 0) >= 0 ? "success.main" : "error.main"}
                                                            sx={{ fontWeight: "bold" }}
                                                        >
                                                            {settlement.realized_pnl?.global?.[currency] ? settlement.realized_pnl.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                        {settlement.origin !== "reward" && (<Typography variant="caption" color="textSecondary">
                                                            {settlement.fees?.global?.[currency] ? settlement.fees.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                        )}
                                                    </Grid>

                                                    {/* Total Value */}
                                                    <Grid item xs={1.5} >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <Tooltip title={settlement.origin.charAt(0).toUpperCase() + settlement.origin.slice(1)}>
                                                                {getSettlementOriginIcon(settlement.origin)}
                                                            </Tooltip>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {settlement.transaction ? settlement.transaction : "-"}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>


                                                    {/* Settlement Date */}
                                                    <Grid item xs={1} sx={{ textAlign: "right" }}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {getFormattedDate(settlement.timestamp)}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {getFormattedTime(settlement.timestamp)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItemButton>
                                        </ListItem>
                                        {index < settlements.length - 1 && <Divider />}
                                    </RecordContextProvider>
                                );
                            })}
                        </List>
                    )}
                </CardContent>
            </AppCard>
        </>
    );
};
